import {
    ChangeEvent,
    Fragment,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import clsx from 'clsx'
import Section from '../../components/Section'
import personApi, {
    GetPersonNetworkImportQueueRes,
    PersonNetworkImportQueueItemDto,
    PersonNetworkImportLogStatusValue,
    PersonNetworkImportStatusValue,
} from '../../api/person/PersonApi'
import LoadingSpinner from '../../uiV2/common/loading/LoadingSpinner'
import Button from '../../uiV2/common/form/inputs/Button'
import CheckboxInput from '../../uiV2/common/form/inputs/CheckboxInput'
import ProfilePic from '../../uiV2/common/person/ProfilePic'
import { formatDurationMs } from '../../utils/dateUtils'
import { formatPersonName } from '../../utils/personUtils'
import Modal from '../../uiV2/common/modals/Modal'
import { toast } from 'sonner'
import {
    PersonNetworkImportLogStatusBadge,
    PersonNetworkImportStatusBadge,
} from './PersonNetworkImportBadges'

const STATUS_OPTIONS: Array<{
    value: PersonNetworkImportStatusValue
    label: string
}> = [
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETE', label: 'Complete' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'CACHE_INVALID', label: 'Cache Invalid' },
    { value: 'EXCLUDED', label: 'Excluded' },
]

const LOG_TERMINAL_STATUSES = new Set<PersonNetworkImportLogStatusValue>([
    'FAILED',
    'COMPLETE',
])

const IMPORT_ELIGIBLE_STATUSES = new Set<PersonNetworkImportStatusValue>([
    'EXCLUDED',
    'FAILED',
    'CACHE_INVALID',
    'COMPLETE',
])

const getDurationColorClass = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return 'text-gray-600'
    }

    if (value <= 60_000) {
        return 'text-green-700'
    }

    if (value <= 300_000) {
        return 'text-yellow-700'
    }

    return 'text-red-700'
}

const getTotalDurationColorClass = (
    value: number | null | undefined,
): string => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return 'text-gray-600'
    }

    if (value > 900_000) {
        return 'text-red-700'
    }

    return 'text-gray-600'
}

const formatDateTime = (value: string): string => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]

    const pad = (num: number) => num.toString().padStart(2, '0')

    const datePart = `${pad(date.getDate())} ${
        months[date.getMonth()]
    } ${date.getFullYear()}`
    const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds(),
    )}`

    return `${datePart} ${timePart}`
}

const formatJsonPayload = (value: string | null): string => {
    if (!value) return ''
    try {
        return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
        return value
    }
}

const PER_PAGE_DEFAULT = 25
const AUTO_REFRESH_INTERVAL_MS = 5_000
const PURGE_CONFIRMATION_TEXT = 'I understand'
const PERSON_TABLE_COLUMN_COUNT = 13
const PERSON_TABLE_CLASS =
    'main-table table-fixed w-full text-[11px] leading-tight break-words'
const PERSON_NESTED_TABLE_CLASS =
    'nested-table table-fixed w-full text-[11px] break-words'
const PERSON_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const PERSON_CELL_CLASS = 'px-2 py-1 align-top min-w-0'

type ExpandableTextBoxProps = {
    text: string | null | undefined
    maxLines?: number
    className?: string
    contentClassName?: string
}

type ManualFailTarget = {
    statusId: string
    logId: string
    currentReceiptHandle: string | null
}

function ExpandableTextBox({
    text,
    maxLines = 5,
    className,
    contentClassName,
}: ExpandableTextBoxProps): React.JSX.Element {
    const [expanded, setExpanded] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setExpanded(false)
    }, [text])

    useEffect(() => {
        if (!text) {
            setIsOverflowing(false)
            return
        }

        if (expanded) {
            return
        }

        const el = textRef.current
        if (!el) {
            return
        }

        const frameId = window.requestAnimationFrame(() => {
            setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
        })

        return () => {
            window.cancelAnimationFrame(frameId)
        }
    }, [text, expanded, maxLines])

    if (!text) {
        return <span>—</span>
    }

    return (
        <div
            className={clsx(
                'rounded border border-gray-200 bg-white/80 p-2',
                className,
            )}
        >
            <div
                ref={textRef}
                className={clsx(
                    'font-mono text-[11px] leading-snug text-gray-800',
                    !expanded && `line-clamp-${maxLines} overflow-hidden`,
                    contentClassName,
                )}
            >
                {text}
            </div>
            {isOverflowing ? (
                <button
                    type="button"
                    className="mt-1 text-xs font-semibold text-blue-600 underline"
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded ? 'Show less' : 'Show more'}
                </button>
            ) : null}
        </div>
    )
}

export default function PersonImportQueue(): React.JSX.Element {
    const [pageNumber, setPageNumber] = useState(1)
    const [selectedStatuses, setSelectedStatuses] = useState<
        PersonNetworkImportStatusValue[]
    >([])
    const [personIdInput, setPersonIdInput] = useState('')
    const [appliedPersonId, setAppliedPersonId] = useState<string | undefined>()
    const [refreshToken, setRefreshToken] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [items, setItems] = useState<PersonNetworkImportQueueItemDto[]>([])
    const [queueMeta, setQueueMeta] = useState<
        Pick<GetPersonNetworkImportQueueRes, 'total' | 'hasMore' | 'perPage'>
    >({ total: 0, hasMore: false, perPage: PER_PAGE_DEFAULT })
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(
        {},
    )
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [showPurgeModal, setShowPurgeModal] = useState(false)
    const [purgeConfirmation, setPurgeConfirmation] = useState('')
    const [purgeLoading, setPurgeLoading] = useState(false)
    const [purgeError, setPurgeError] = useState<string | undefined>()
    const [manualFailModalOpen, setManualFailModalOpen] = useState(false)
    const [manualFailTarget, setManualFailTarget] = useState<
        ManualFailTarget | undefined
    >(undefined)
    const [manualFailReceiptHandle, setManualFailReceiptHandle] = useState('')
    const [manualFailSetStatusToFailed, setManualFailSetStatusToFailed] =
        useState(true)
    const [manualFailLoading, setManualFailLoading] = useState(false)
    const [manualFailError, setManualFailError] = useState<string | undefined>()
    const [importingPersonIds, setImportingPersonIds] = useState<
        Record<string, boolean>
    >({})
    const loadingRef = useRef(false)

    const appliedStatuses = useMemo(
        () => (selectedStatuses.length > 0 ? selectedStatuses : undefined),
        [selectedStatuses],
    )

    const purgeConfirmationMatches =
        purgeConfirmation === PURGE_CONFIRMATION_TEXT

    useEffect(() => {
        let ignore = false
        const run = async () => {
            setLoading(true)
            loadingRef.current = true
            setError(undefined)
            try {
                const res = await personApi.getPersonNetworkImportQueue({
                    pageNumber,
                    perPage: PER_PAGE_DEFAULT,
                    statuses: appliedStatuses,
                    personId: appliedPersonId,
                })
                if (ignore) return
                setItems(res.items)
                setQueueMeta({
                    total: res.total,
                    hasMore: res.hasMore,
                    perPage: res.perPage ?? PER_PAGE_DEFAULT,
                })
                setExpandedRows((prev) => {
                    const next: Record<string, boolean> = {}
                    for (const item of res.items) {
                        if (prev[item.id]) {
                            next[item.id] = true
                        }
                    }
                    return next
                })
            } catch (err) {
                if (ignore) return
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Failed to load person import queue'
                setError(message)
            } finally {
                if (!ignore) {
                    setLoading(false)
                    loadingRef.current = false
                }
            }
        }

        void run()

        return () => {
            loadingRef.current = false
            ignore = true
        }
    }, [pageNumber, appliedStatuses, appliedPersonId, refreshToken])

    useEffect(() => {
        if (!autoRefresh) {
            return
        }

        const intervalId = window.setInterval(() => {
            if (!loadingRef.current) {
                setRefreshToken((token) => token + 1)
            }
        }, AUTO_REFRESH_INTERVAL_MS)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [autoRefresh])

    const handleToggleStatus = (
        status: PersonNetworkImportStatusValue,
        checked: boolean,
    ) => {
        setSelectedStatuses((prev) => {
            if (checked) {
                if (prev.includes(status)) {
                    return prev
                }
                return [...prev, status]
            }
            return prev.filter((value) => value !== status)
        })
        setPageNumber(1)
    }

    const handleSubmitPersonId = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const trimmed = personIdInput.trim()
        setAppliedPersonId(trimmed ? trimmed : undefined)
        setPageNumber(1)
    }

    const handleClearPersonId = () => {
        setPersonIdInput('')
        setAppliedPersonId(undefined)
        setPageNumber(1)
    }

    const handleAutoRefreshChange = (checked: boolean) => {
        setAutoRefresh(checked)
        if (checked && !loadingRef.current) {
            setRefreshToken((token) => token + 1)
        }
    }

    const handleOpenPurgeModal = () => {
        setPurgeConfirmation('')
        setPurgeError(undefined)
        setShowPurgeModal(true)
    }

    const handleClosePurgeModal = () => {
        if (purgeLoading) {
            return
        }
        setShowPurgeModal(false)
        setPurgeConfirmation('')
        setPurgeError(undefined)
    }

    const handlePurgeConfirmationChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        setPurgeConfirmation(event.target.value)
        if (purgeError) {
            setPurgeError(undefined)
        }
    }

    const handleConfirmPurge = async () => {
        if (!purgeConfirmationMatches || purgeLoading) {
            return
        }

        setPurgeLoading(true)
        setPurgeError(undefined)

        try {
            const res = await personApi.purgePersonNetworkImportQueue({
                confirmation: PURGE_CONFIRMATION_TEXT,
            })
            toast.success(
                res.message ||
                    'Queue purge requested. Messages will clear shortly.',
            )
            setShowPurgeModal(false)
            setPurgeConfirmation('')
            setRefreshToken((token) => token + 1)
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Failed to purge person import queue.'
            setPurgeError(message)
            toast.error(message)
        } finally {
            setPurgeLoading(false)
        }
    }

    const handleOpenManualFailModal = (
        statusId: string,
        log: PersonNetworkImportQueueItemDto['logs'][number],
    ) => {
        setManualFailTarget({
            statusId,
            logId: log.id,
            currentReceiptHandle: log.sqsReceiptHandle,
        })
        setManualFailReceiptHandle(log.sqsReceiptHandle ?? '')
        setManualFailSetStatusToFailed(true)
        setManualFailError(undefined)
        setManualFailModalOpen(true)
    }

    const handleCloseManualFailModal = () => {
        if (manualFailLoading) {
            return
        }
        setManualFailModalOpen(false)
        setManualFailTarget(undefined)
        setManualFailReceiptHandle('')
        setManualFailSetStatusToFailed(true)
        setManualFailError(undefined)
    }

    const handleManualFailReceiptHandleChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        setManualFailReceiptHandle(event.target.value)
        if (manualFailError) {
            setManualFailError(undefined)
        }
    }

    const handleManualFailSetStatusChange = (checked: boolean) => {
        setManualFailSetStatusToFailed(checked)
    }

    const handleConfirmManualFail = async () => {
        if (!manualFailTarget || manualFailLoading) {
            return
        }

        setManualFailLoading(true)
        setManualFailError(undefined)

        const trimmedHandle = manualFailReceiptHandle.trim()

        try {
            const res = await personApi.manualFailPersonNetworkImportLog({
                statusId: manualFailTarget.statusId,
                logId: manualFailTarget.logId,
                setStatusToFailed: manualFailSetStatusToFailed,
                sqsReceiptHandle: trimmedHandle ? trimmedHandle : undefined,
            })
            toast.success(
                res.message || 'Log manually marked as failed successfully.',
            )
            setManualFailModalOpen(false)
            setManualFailTarget(undefined)
            setManualFailReceiptHandle('')
            setManualFailSetStatusToFailed(true)
            setRefreshToken((token) => token + 1)
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Failed to manually fail log item.'
            setManualFailError(message)
            toast.error(message)
        } finally {
            setManualFailLoading(false)
        }
    }

    const handleImportNetwork = async (
        item: PersonNetworkImportQueueItemDto,
    ) => {
        const personId = item.personId?.trim()

        if (!personId || importingPersonIds[personId]) {
            return
        }

        setImportingPersonIds((prev) => ({ ...prev, [personId]: true }))

        try {
            const res = await personApi.importPersonNetwork({
                personId,
                allowCompleteToBeReprocessed: true,
            })
            toast.success(
                res.message ||
                    'Network import requested. It will start shortly.',
            )
            setRefreshToken((token) => token + 1)
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Failed to queue network import.'
            toast.error(message)
        } finally {
            setImportingPersonIds((prev) => {
                const next = { ...prev }
                delete next[personId]
                return next
            })
        }
    }

    const toggleRow = (id: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [id]: !prev[id],
        }))
    }

    const perPage = queueMeta.perPage || PER_PAGE_DEFAULT
    const totalPages = useMemo(() => {
        if (queueMeta.total <= 0) return 1
        return Math.max(1, Math.ceil(queueMeta.total / perPage))
    }, [queueMeta.total, perPage])

    const canGoPrev = pageNumber > 1
    const hasNextPage =
        queueMeta.hasMore || pageNumber * perPage < queueMeta.total
    const canGoNext = hasNextPage

    const effectivePageNumber = Math.max(1, Math.min(pageNumber, totalPages))
    const visibleRangeStart =
        queueMeta.total === 0 ? 0 : (effectivePageNumber - 1) * perPage + 1
    const visibleRangeEndRaw =
        queueMeta.total === 0
            ? 0
            : (effectivePageNumber - 1) * perPage + items.length
    const visibleRangeEnd =
        queueMeta.total === 0
            ? 0
            : Math.max(visibleRangeStart, visibleRangeEndRaw)

    const summaryText =
        queueMeta.total === 0
            ? 'Showing 0 of 0'
            : `Showing ${visibleRangeStart.toLocaleString()} – ${visibleRangeEnd.toLocaleString()} of ${queueMeta.total.toLocaleString()}`

    return (
        <div className="mt-6">
            <Section subtitle="Person Import Queue">
                <div className="flex flex-wrap items-end gap-4">
                    <form
                        onSubmit={handleSubmitPersonId}
                        className="flex items-end gap-2"
                    >
                        <label className="flex flex-col text-sm font-medium text-gray-700">
                            Person ID
                            <input
                                type="text"
                                value={personIdInput}
                                onChange={(event) =>
                                    setPersonIdInput(event.target.value)
                                }
                                className="mt-1 w-56 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="PERxxxxxxxxxxxxx"
                            />
                        </label>
                        <Button
                            type="submit"
                            style="primary"
                            size="small"
                            enabled={!loading}
                        >
                            Apply
                        </Button>
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={handleClearPersonId}
                            enabled={
                                !loading &&
                                (Boolean(personIdInput) ||
                                    appliedPersonId !== undefined)
                            }
                        >
                            Clear
                        </Button>
                    </form>
                    <div className="flex flex-wrap items-center gap-3">
                        {STATUS_OPTIONS.map((option) => (
                            <CheckboxInput
                                key={option.value}
                                id={`status-${option.value}`}
                                label={option.label}
                                checked={selectedStatuses.includes(
                                    option.value,
                                )}
                                onChange={(checked) =>
                                    handleToggleStatus(option.value, checked)
                                }
                                className="text-xs"
                            />
                        ))}
                        <CheckboxInput
                            id="person-import-queue-auto-refresh"
                            label="Auto Refresh (5s)"
                            checked={autoRefresh}
                            onChange={handleAutoRefreshChange}
                            className="text-xs"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() =>
                                setRefreshToken((token) => token + 1)
                            }
                            enabled={!loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="button"
                            style="danger"
                            size="small"
                            onClick={handleOpenPurgeModal}
                            enabled={!loading}
                        >
                            Purge Queue
                        </Button>
                    </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                    <div>{summaryText}</div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => setPageNumber((value) => value - 1)}
                            enabled={canGoPrev && !loading}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {pageNumber} / {totalPages}
                        </span>
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => setPageNumber((value) => value + 1)}
                            enabled={canGoNext && !loading}
                        >
                            Next
                        </Button>
                    </div>
                </div>
                {error ? (
                    <div className="mt-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}
                <div className="relative mt-4">
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                            <LoadingSpinner captions={[]} spinnerSize={32} />
                        </div>
                    ) : null}
                    <div className="overflow-x-auto">
                        <table className={PERSON_TABLE_CLASS}>
                            <thead>
                                <tr className="text-[10px] uppercase">
                                    <th className={PERSON_HEADER_CELL_CLASS} />
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        ID
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Person
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Person ID
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Status
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Latest Log Status
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Latest Log Processing Time
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Latest Log Total Time
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Error Message
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Created At
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Updated At
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Created By
                                    </th>
                                    <th className={PERSON_HEADER_CELL_CLASS}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && !loading ? (
                                    <tr>
                                        <td
                                            colSpan={PERSON_TABLE_COLUMN_COUNT}
                                            className="py-4 text-center"
                                        >
                                            No import runs found.
                                        </td>
                                    </tr>
                                ) : null}
                                {items.map((item) => {
                                    const expanded = Boolean(
                                        expandedRows[item.id],
                                    )
                                    const latestLog =
                                        item.logs.length > 0
                                            ? item.logs.reduce(
                                                  (latest, log) =>
                                                      log.updatedAt >
                                                      latest.updatedAt
                                                          ? log
                                                          : latest,
                                                  item.logs[0],
                                              )
                                            : undefined
                                    const isImportingNetwork = Boolean(
                                        importingPersonIds[item.personId],
                                    )
                                    return (
                                        <Fragment key={item.id}>
                                            <tr
                                                className={clsx(
                                                    expanded &&
                                                        'border-l-4 border-blue-400 bg-blue-50/40',
                                                )}
                                            >
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'w-14',
                                                    )}
                                                >
                                                    <button
                                                        type="button"
                                                        className="text-xs font-semibold text-blue-600 underline"
                                                        onClick={() =>
                                                            toggleRow(item.id)
                                                        }
                                                    >
                                                        {expanded
                                                            ? 'Hide logs'
                                                            : 'Show logs'}
                                                    </button>
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'font-mono text-[10px] break-all',
                                                    )}
                                                >
                                                    {item.id}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'min-w-[220px]',
                                                    )}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <ProfilePic
                                                            src={
                                                                item.person
                                                                    ?.linkedInProfilePicUrl
                                                            }
                                                            size={32}
                                                        />
                                                        <div className="flex flex-col">
                                                            <span className="text-[11px] font-semibold leading-tight">
                                                                {item.person
                                                                    ? item
                                                                          .person
                                                                          .fullName ||
                                                                      formatPersonName(
                                                                          {
                                                                              firstName:
                                                                                  item
                                                                                      .person
                                                                                      .firstName,
                                                                              middleName:
                                                                                  item
                                                                                      .person
                                                                                      .middleName,
                                                                              lastName:
                                                                                  item
                                                                                      .person
                                                                                      .lastName,
                                                                          },
                                                                      )
                                                                    : 'Unknown'}
                                                            </span>
                                                            {item.person
                                                                ?.headline ? (
                                                                <span className="text-[10px] text-gray-500">
                                                                    {
                                                                        item
                                                                            .person
                                                                            .headline
                                                                    }
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'font-mono text-[10px] break-all',
                                                    )}
                                                >
                                                    {item.personId}
                                                </td>
                                                <td
                                                    className={
                                                        PERSON_CELL_CLASS
                                                    }
                                                >
                                                    <PersonNetworkImportStatusBadge
                                                        status={item.status}
                                                    />
                                                </td>
                                                <td
                                                    className={
                                                        PERSON_CELL_CLASS
                                                    }
                                                >
                                                    {latestLog ? (
                                                        <PersonNetworkImportLogStatusBadge
                                                            status={
                                                                latestLog.status
                                                            }
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'font-semibold text-right',
                                                        getDurationColorClass(
                                                            latestLog?.timeInProcessingMs,
                                                        ),
                                                    )}
                                                >
                                                    {latestLog
                                                        ? formatDurationMs(
                                                              latestLog.timeInProcessingMs,
                                                          )
                                                        : '—'}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'font-semibold text-right',
                                                        getTotalDurationColorClass(
                                                            latestLog?.timeTotalMs,
                                                        ),
                                                    )}
                                                >
                                                    {latestLog
                                                        ? formatDurationMs(
                                                              latestLog.timeTotalMs,
                                                          )
                                                        : '—'}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'max-w-[220px]',
                                                    )}
                                                >
                                                    <ExpandableTextBox
                                                        text={item.errMsg}
                                                        className="min-w-0"
                                                        contentClassName="whitespace-pre-wrap break-words"
                                                    />
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'text-center leading-tight',
                                                    )}
                                                >
                                                    {formatDateTime(
                                                        item.createdAt,
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'text-center leading-tight',
                                                    )}
                                                >
                                                    {formatDateTime(
                                                        item.updatedAt,
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'text-[11px] break-words',
                                                    )}
                                                >
                                                    {item.createdByEmail ??
                                                        item.createdBy ??
                                                        '—'}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        PERSON_CELL_CLASS,
                                                        'text-center',
                                                    )}
                                                >
                                                    {IMPORT_ELIGIBLE_STATUSES.has(
                                                        item.status,
                                                    ) ? (
                                                        <Button
                                                            type="button"
                                                            style="primary"
                                                            size="small"
                                                            onClick={() =>
                                                                void handleImportNetwork(
                                                                    item,
                                                                )
                                                            }
                                                            enabled={
                                                                !loading &&
                                                                !isImportingNetwork
                                                            }
                                                        >
                                                            {isImportingNetwork
                                                                ? 'Importing...'
                                                                : 'Import Network'}
                                                        </Button>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </tr>
                                            {expanded ? (
                                                <tr className="bg-blue-50/20 text-[11px]">
                                                    <td
                                                        colSpan={
                                                            PERSON_TABLE_COLUMN_COUNT
                                                        }
                                                    >
                                                        <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 p-2">
                                                            <div className="mb-2 text-[11px] font-semibold text-blue-700">
                                                                Log History
                                                            </div>
                                                            <table
                                                                className={
                                                                    PERSON_NESTED_TABLE_CLASS
                                                                }
                                                            >
                                                                <thead>
                                                                    <tr className="text-[10px] uppercase tracking-wide text-blue-800">
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Log
                                                                            ID
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Status
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Error
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SQS
                                                                            Msg
                                                                            ID
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SQS
                                                                            Receipt
                                                                            Handle
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SQS
                                                                            Payload
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SFN
                                                                            Execution
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Created
                                                                            At
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Updated
                                                                            At
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Time
                                                                            Waiting
                                                                            to
                                                                            Queue
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Time
                                                                            in
                                                                            Queue
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Time
                                                                            in
                                                                            Processing
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Total
                                                                            Time
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                PERSON_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Actions
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.logs
                                                                        .length ===
                                                                    0 ? (
                                                                        <tr>
                                                                            <td
                                                                                colSpan={
                                                                                    14
                                                                                }
                                                                                className="py-4 text-center text-[10px] text-blue-700"
                                                                            >
                                                                                No
                                                                                logs
                                                                                recorded.
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        item.logs.map(
                                                                            (
                                                                                log,
                                                                            ) => (
                                                                                <tr
                                                                                    key={
                                                                                        log.id
                                                                                    }
                                                                                    className="border-t border-blue-100 last:border-b"
                                                                                >
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'font-mono text-[10px] break-all',
                                                                                        )}
                                                                                    >
                                                                                        {
                                                                                            log.id
                                                                                        }
                                                                                    </td>
                                                                                    <td
                                                                                        className={
                                                                                            PERSON_CELL_CLASS
                                                                                        }
                                                                                    >
                                                                                        <PersonNetworkImportLogStatusBadge
                                                                                            status={
                                                                                                log.status
                                                                                            }
                                                                                        />
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'max-w-[200px]',
                                                                                        )}
                                                                                    >
                                                                                        <ExpandableTextBox
                                                                                            text={
                                                                                                log.errMsg
                                                                                            }
                                                                                            className="min-w-0"
                                                                                            contentClassName="whitespace-pre-wrap break-words"
                                                                                        />
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'font-mono text-[10px] break-all',
                                                                                        )}
                                                                                    >
                                                                                        {log.sqsMsgId ??
                                                                                            '—'}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'max-w-[200px]',
                                                                                        )}
                                                                                    >
                                                                                        <ExpandableTextBox
                                                                                            text={
                                                                                                log.sqsReceiptHandle
                                                                                            }
                                                                                            className="min-w-0"
                                                                                            contentClassName="break-all"
                                                                                        />
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'max-w-[200px]',
                                                                                        )}
                                                                                    >
                                                                                        <ExpandableTextBox
                                                                                            text={formatJsonPayload(
                                                                                                log.sqsMsgPayload,
                                                                                            )}
                                                                                            maxLines={
                                                                                                3
                                                                                            }
                                                                                            className="min-w-0"
                                                                                        />
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'font-mono text-[10px] break-all',
                                                                                        )}
                                                                                    >
                                                                                        {log.sfnExeName ? (
                                                                                            log.sfnExecutionUrl ? (
                                                                                                <a
                                                                                                    href={
                                                                                                        log.sfnExecutionUrl
                                                                                                    }
                                                                                                    target="_blank"
                                                                                                    rel="noopener noreferrer"
                                                                                                    className="text-blue-600 underline break-all"
                                                                                                >
                                                                                                    {
                                                                                                        log.sfnExeName
                                                                                                    }
                                                                                                </a>
                                                                                            ) : (
                                                                                                log.sfnExeName
                                                                                            )
                                                                                        ) : (
                                                                                            '—'
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-center leading-tight',
                                                                                        )}
                                                                                    >
                                                                                        {formatDateTime(
                                                                                            log.createdAt,
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-center leading-tight',
                                                                                        )}
                                                                                    >
                                                                                        {formatDateTime(
                                                                                            log.updatedAt,
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-right',
                                                                                        )}
                                                                                    >
                                                                                        {formatDurationMs(
                                                                                            log.timeWaitingToQueueMs,
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-right',
                                                                                        )}
                                                                                    >
                                                                                        {formatDurationMs(
                                                                                            log.timeInQueueMs,
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-right',
                                                                                        )}
                                                                                    >
                                                                                        {formatDurationMs(
                                                                                            log.timeInProcessingMs,
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-right',
                                                                                        )}
                                                                                    >
                                                                                        {formatDurationMs(
                                                                                            log.timeTotalMs,
                                                                                        )}
                                                                                    </td>
                                                                                    <td
                                                                                        className={clsx(
                                                                                            PERSON_CELL_CLASS,
                                                                                            'text-center',
                                                                                        )}
                                                                                    >
                                                                                        {LOG_TERMINAL_STATUSES.has(
                                                                                            log.status,
                                                                                        ) ? (
                                                                                            '—'
                                                                                        ) : (
                                                                                            <Button
                                                                                                type="button"
                                                                                                style="danger"
                                                                                                size="small"
                                                                                                onClick={() =>
                                                                                                    handleOpenManualFailModal(
                                                                                                        item.id,
                                                                                                        log,
                                                                                                    )
                                                                                                }
                                                                                                enabled={
                                                                                                    !manualFailLoading
                                                                                                }
                                                                                            >
                                                                                                Fail
                                                                                                Item
                                                                                            </Button>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ),
                                                                        )
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Section>
            {manualFailModalOpen && manualFailTarget ? (
                <Modal
                    title="Manually Fail Log Item"
                    onClose={handleCloseManualFailModal}
                    allowUserToClose={!manualFailLoading}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                size="small"
                                onClick={handleCloseManualFailModal}
                                enabled={!manualFailLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="danger"
                                size="small"
                                onClick={handleConfirmManualFail}
                                enabled={!manualFailLoading}
                            >
                                Fail Item
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            This will mark log{' '}
                            <span className="font-semibold">
                                {manualFailTarget.logId}
                            </span>{' '}
                            as FAILED with the message “Manually failed”.
                        </p>
                        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            Are you sure? This action is intended to realign the
                            database with SQS and cannot be undone.
                        </div>
                        <div className="flex flex-col gap-1">
                            <label
                                htmlFor="manual-fail-sqs-receipt"
                                className="text-sm font-medium text-gray-700"
                            >
                                SQS Receipt Handle (optional)
                            </label>
                            <input
                                id="manual-fail-sqs-receipt"
                                type="text"
                                value={manualFailReceiptHandle}
                                onChange={handleManualFailReceiptHandleChange}
                                disabled={manualFailLoading}
                                placeholder={
                                    manualFailTarget.currentReceiptHandle
                                        ? 'Leave blank to use recorded handle'
                                        : 'Paste the handle if available'
                                }
                                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <span className="text-xs text-gray-500 break-all">
                                {manualFailTarget.currentReceiptHandle
                                    ? `Recorded handle: ${manualFailTarget.currentReceiptHandle}`
                                    : 'Provide a handle if you retrieved it from AWS.'}
                            </span>
                        </div>
                        <CheckboxInput
                            id="manual-fail-set-status"
                            label="Also mark the related status row as FAILED"
                            checked={manualFailSetStatusToFailed}
                            onChange={handleManualFailSetStatusChange}
                            disabled={manualFailLoading}
                            description="Keeps the status record in sync with the log entry. Disable only if you need to preserve the current status."
                        />
                        {manualFailError ? (
                            <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {manualFailError}
                            </div>
                        ) : null}
                    </div>
                </Modal>
            ) : null}
            {showPurgeModal ? (
                <Modal
                    title="Purge Person Import Queue"
                    onClose={handleClosePurgeModal}
                    allowUserToClose={!purgeLoading}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                size="small"
                                onClick={handleClosePurgeModal}
                                enabled={!purgeLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="danger"
                                size="small"
                                onClick={handleConfirmPurge}
                                enabled={
                                    purgeConfirmationMatches && !purgeLoading
                                }
                            >
                                Purge Queue
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            Purging the queue will delete all in-flight and
                            pending person import requests from SQS. Imports in
                            progress may fail and will need to be retriggered.
                        </p>
                        <p className="text-sm text-gray-700">
                            To confirm, type{' '}
                            <span className="font-semibold">
                                {PURGE_CONFIRMATION_TEXT}
                            </span>{' '}
                            exactly (case sensitive) in the field below, then
                            press Purge Queue.
                        </p>
                        <input
                            type="text"
                            value={purgeConfirmation}
                            onChange={handlePurgeConfirmationChange}
                            autoFocus
                            disabled={purgeLoading}
                            placeholder={PURGE_CONFIRMATION_TEXT}
                            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                        <p className="text-xs text-gray-500">
                            AWS only allows a purge request once every 60
                            seconds. If a purge was triggered recently, retrying
                            too soon will fail.
                        </p>
                        {purgeError ? (
                            <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {purgeError}
                            </div>
                        ) : null}
                    </div>
                </Modal>
            ) : null}
        </div>
    )
}
